import React, { useEffect, useState } from "react";
import { ComponentProps } from "../../models/models";
import { ProfilePayload } from "../../models/userModels";
import { NewAdvertisement } from "./elements";
import { useAdvertisementData } from "./hooks";
import cn from "classnames";
import { getSpecialistRole } from "../ProfileSpecialist/utils";
import { TranslationObject } from "../../models/translationModal";
import { useLocation, useNavigate } from "react-router-dom";
import { InDevelopmentNotification } from "../../components/Modals/InDevelopmentNotification/InDevelopmentNotification";

interface AdvertisementProps extends ComponentProps {
    profile?: ProfilePayload;
    entities?: TranslationObject;
}

export const Advertisement: React.FC<AdvertisementProps> = ({
    profile,
    entities,
    currentLanguage,
}) => {
    const [newAdvertisement, setNewAdvertisement] = useState<{
        show: boolean;
        type: "banner" | "top" | "posts";
    }>({
        show: false,
        type: "banner",
    });

    const [showInDevelopmentNotification, setShowInDevelopmentNotification] =
        useState<boolean>(false);

    const { myBanner, myTop, myPosts, companyEmployees } =
        useAdvertisementData(profile?.id);

    const specialistRole = getSpecialistRole(profile, entities);
    const isSpecialist = profile?.user_role === "specialist";

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isSpecialist) {
            navigate(`/${currentLanguage}/customer/notfound`);
        }
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    useEffect(() => {
        if (location.state?.openType === "posts") {
            setNewAdvertisement({ show: true, type: "posts" });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, []);

    if (newAdvertisement.show) {
        return (
            <NewAdvertisement
                entities={entities}
                onClose={() => {
                    setNewAdvertisement({
                        show: false,
                        type: "banner",
                    });

                    if (newAdvertisement.type === "posts") {
                        navigate(`/${currentLanguage}/posts`);
                    }
                }}
                type={newAdvertisement.type}
                role={specialistRole?.role}
                profile={profile}
            />
        );
    }

    return (
        <>
            <InDevelopmentNotification
                entities={entities}
                visible={showInDevelopmentNotification}
                setVisible={(visible) =>
                    setShowInDevelopmentNotification(visible)
                }
                isSpecialist={true}
            />
            <main>
                <div className="spec-page">
                    <div className="spec-page__container container">
                        <section className="spec-page__offers specialist-offers">
                            <h1 className="spec-page__heading title title--f46">
                                {entities?.advertising_link_value?.value}
                            </h1>
                            <ul className="specialist-offers__list">
                                <li className="specialist-offers__item">
                                    <article className="offer-card">
                                        <div className="offer-card__info">
                                            {specialistRole?.role ===
                                                "private person" && (
                                                <span
                                                    className={cn("tag", {
                                                        "tag--active":
                                                            myPosts?.status ===
                                                            "active",
                                                        "tag--pending":
                                                            myPosts?.status ===
                                                            "pending",
                                                    })}
                                                >
                                                    {myPosts?.status ===
                                                    "active"
                                                        ? entities
                                                              ?.advertisement_active
                                                              ?.value
                                                        : myPosts?.status ===
                                                          "pending"
                                                        ? entities
                                                              ?.advertisement_in_queue
                                                              ?.value
                                                        : entities
                                                              ?.advertisement_no_activated
                                                              ?.value}
                                                </span>
                                            )}
                                            <h2 className="offer-card__title title title--f24">
                                                {
                                                    entities?.devi_info_title
                                                        ?.value
                                                }
                                            </h2>
                                            <p className="text">
                                                {
                                                    entities
                                                        ?.devi_info_description
                                                        ?.value
                                                }
                                            </p>
                                        </div>
                                        <div className="offer-card__column">
                                            {!myPosts?.status && (
                                                <button
                                                    className="btn btn--green btn--rounded offer-card__btn"
                                                    type="button"
                                                    onClick={() =>
                                                        setNewAdvertisement({
                                                            show: true,
                                                            type: "posts",
                                                        })
                                                    }
                                                >
                                                    {
                                                        entities
                                                            ?.advertisement_activate
                                                            ?.value
                                                    }
                                                </button>
                                            )}
                                            {myPosts?.status === "active" && (
                                                <div className="description">
                                                    <dl>
                                                        <div className="description__row mb-2">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_end
                                                                        ?.value
                                                                }
                                                            </dt>
                                                            <dd>
                                                                {myPosts.endsAt}
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                    <button
                                                        className="btn btn--green btn--rounded offer-card__btn"
                                                        type="button"
                                                        onClick={() =>
                                                            setNewAdvertisement(
                                                                {
                                                                    show: true,
                                                                    type: "posts",
                                                                }
                                                            )
                                                        }
                                                    >
                                                        {
                                                            entities
                                                                ?.devi_extend
                                                                ?.value
                                                        }
                                                    </button>
                                                </div>
                                            )}
                                            {myPosts?.status === "pending" && (
                                                <div className="description">
                                                    <dl>
                                                        <div className="description__row">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_queue_up_to
                                                                        ?.value
                                                                }
                                                            </dt>
                                                        </div>
                                                    </dl>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                </li>
                                <li className="specialist-offers__item">
                                    <article className="offer-card">
                                        <div className="offer-card__info">
                                            {specialistRole?.role ===
                                                "private person" && (
                                                <span
                                                    className={cn("tag", {
                                                        "tag--active":
                                                            myBanner.status ===
                                                            "active",
                                                        "tag--pending":
                                                            myBanner.status ===
                                                            "pending",
                                                    })}
                                                >
                                                    {myBanner.status ===
                                                    "active"
                                                        ? entities
                                                              ?.advertisement_active
                                                              ?.value
                                                        : myBanner.status ===
                                                          "pending"
                                                        ? entities
                                                              ?.advertisement_in_queue
                                                              ?.value
                                                        : entities
                                                              ?.advertisement_no_activated
                                                              ?.value}
                                                </span>
                                            )}
                                            <h2 className="offer-card__title title title--f24">
                                                {
                                                    entities
                                                        ?.advertisement_on_platform
                                                        ?.value
                                                }
                                            </h2>
                                            <p className="text">
                                                {
                                                    entities
                                                        ?.profile_add_services_photo
                                                        ?.value
                                                }
                                            </p>
                                        </div>
                                        <div className="offer-card__column">
                                            {!myBanner?.status && (
                                                <button
                                                    className="btn btn--green btn--rounded offer-card__btn"
                                                    type="button"
                                                    onClick={() =>
                                                        setNewAdvertisement({
                                                            show: true,
                                                            type: "banner",
                                                        })
                                                    }
                                                >
                                                    {
                                                        entities
                                                            ?.advertisement_activate
                                                            ?.value
                                                    }
                                                </button>
                                            )}
                                            {myBanner.status === "active" && (
                                                <div className="description">
                                                    <dl>
                                                        <div className="description__row">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_start
                                                                        ?.value
                                                                }
                                                            </dt>
                                                            <dd>
                                                                {
                                                                    myBanner.startsAt
                                                                }
                                                            </dd>
                                                        </div>
                                                        <div className="description__row">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_end
                                                                        ?.value
                                                                }
                                                            </dt>
                                                            <dd>
                                                                {
                                                                    myBanner.endsAt
                                                                }
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            )}
                                            {myBanner.status === "pending" && (
                                                <div className="description">
                                                    <dl>
                                                        <div className="description__row">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_queue_up_to
                                                                        ?.value
                                                                }
                                                            </dt>
                                                            <dd>
                                                                {
                                                                    myBanner.startsAt
                                                                }
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                </li>
                                <li className="specialist-offers__item">
                                    <article className="offer-card">
                                        <div className="offer-card__info">
                                            {specialistRole?.role ===
                                                "private person" && (
                                                <span
                                                    className={cn("tag", {
                                                        "tag--active":
                                                            myTop.status ===
                                                            "active",
                                                        "tag--pending":
                                                            myTop.status ===
                                                            "pending",
                                                    })}
                                                >
                                                    {myTop.status === "active"
                                                        ? entities
                                                              ?.advertisement_active
                                                              ?.value
                                                        : myTop.status ===
                                                          "pending"
                                                        ? entities
                                                              ?.advertisement_in_queue
                                                              ?.value
                                                        : entities
                                                              ?.advertisement_no_activated
                                                              ?.value}
                                                </span>
                                            )}
                                            <h2 className="offer-card__title title title--f24">
                                                {
                                                    entities
                                                        ?.advertisement_rise_to_top
                                                        ?.value
                                                }
                                            </h2>
                                            <p className="text">
                                                {
                                                    entities
                                                        ?.rise_in_the_list_price_block_description
                                                        ?.value
                                                }
                                            </p>
                                        </div>
                                        <div className="offer-card__column">
                                            {!myTop?.status && (
                                                <button
                                                    className="btn btn--green btn--rounded offer-card__btn"
                                                    type="button"
                                                    onClick={() =>
                                                        setNewAdvertisement({
                                                            show: true,
                                                            type: "top",
                                                        })
                                                    }
                                                >
                                                    {
                                                        entities
                                                            ?.advertisement_activate
                                                            ?.value
                                                    }
                                                </button>
                                            )}
                                            {myTop.status === "active" && (
                                                <div className="description">
                                                    <dl>
                                                        <div className="description__row">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_start
                                                                        ?.value
                                                                }
                                                            </dt>
                                                            <dd>
                                                                {myTop.startsAt}
                                                            </dd>
                                                        </div>
                                                        <div className="description__row">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_end
                                                                        ?.value
                                                                }
                                                            </dt>
                                                            <dd>
                                                                {myTop.endsAt}
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            )}
                                            {myTop.status === "pending" && (
                                                <div className="description">
                                                    <dl>
                                                        <div className="description__row">
                                                            <dt>
                                                                {
                                                                    entities
                                                                        ?.advertisement_queue_up_to
                                                                        ?.value
                                                                }
                                                            </dt>
                                                            <dd>
                                                                {myTop.startsAt}
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                </li>
                            </ul>
                            {specialistRole?.role === "manager" &&
                                !!companyEmployees.length && (
                                    <div className="specialist-offers__block">
                                        <h3 className="specialist-offers__subtitle title title--f24">
                                            {
                                                entities
                                                    ?.order_company_employees
                                                    ?.value
                                            }
                                        </h3>
                                        <div className="table-large">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th scope="col">
                                                            {
                                                                entities
                                                                    ?.advertisement_employer
                                                                    ?.value
                                                            }
                                                        </th>
                                                        <th scope="col">
                                                            {
                                                                entities
                                                                    ?.advertisement_on_platform
                                                                    ?.value
                                                            }
                                                        </th>
                                                        <th scope="col">
                                                            {
                                                                entities
                                                                    ?.advertisement_rise_to_top
                                                                    ?.value
                                                            }
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {companyEmployees.map(
                                                        ({
                                                            id,
                                                            name,
                                                            familyName,
                                                            banner,
                                                            top,
                                                        }) => (
                                                            <tr key={id}>
                                                                <th scope="row">
                                                                    {`${name} ${familyName}`}
                                                                </th>
                                                                {(banner && (
                                                                    <td>
                                                                        <span
                                                                            className={cn(
                                                                                "tag",
                                                                                {
                                                                                    "tag--active":
                                                                                        banner?.status ===
                                                                                        "active",
                                                                                    "tag--pending":
                                                                                        banner?.status ===
                                                                                        "pending",
                                                                                }
                                                                            )}
                                                                        >
                                                                            {banner?.status ===
                                                                            "active"
                                                                                ? "Активна"
                                                                                : banner?.status ===
                                                                                  "pending"
                                                                                ? "В очереди"
                                                                                : ""}
                                                                        </span>
                                                                        <div className="description">
                                                                            {banner.status ===
                                                                                "active" && (
                                                                                <dl>
                                                                                    <div className="description__row">
                                                                                        <dt>
                                                                                            {
                                                                                                entities
                                                                                                    ?.advertisement_start
                                                                                                    ?.value
                                                                                            }
                                                                                        </dt>
                                                                                        <dd>
                                                                                            {
                                                                                                banner?.startsAt
                                                                                            }
                                                                                        </dd>
                                                                                    </div>
                                                                                    <div className="description__row">
                                                                                        <dt>
                                                                                            {
                                                                                                entities
                                                                                                    ?.advertisement_end
                                                                                                    ?.value
                                                                                            }
                                                                                        </dt>
                                                                                        <dd>
                                                                                            {
                                                                                                banner?.endsAt
                                                                                            }
                                                                                        </dd>
                                                                                    </div>
                                                                                </dl>
                                                                            )}
                                                                            {banner.status ===
                                                                                "pending" && (
                                                                                <dl>
                                                                                    <div className="description__row">
                                                                                        <dt>
                                                                                            {
                                                                                                entities
                                                                                                    ?.common_to
                                                                                                    ?.value
                                                                                            }
                                                                                        </dt>
                                                                                        <dd>
                                                                                            {
                                                                                                banner?.startsAt
                                                                                            }
                                                                                        </dd>
                                                                                    </div>
                                                                                </dl>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                )) || (
                                                                    <td className="is-empty" />
                                                                )}
                                                                {(top && (
                                                                    <td>
                                                                        <span
                                                                            className={cn(
                                                                                "tag",
                                                                                {
                                                                                    "tag--active":
                                                                                        top?.status ===
                                                                                        "active",
                                                                                    "tag--pending":
                                                                                        top?.status ===
                                                                                        "pending",
                                                                                }
                                                                            )}
                                                                        >
                                                                            {top?.status ===
                                                                            "active"
                                                                                ? entities
                                                                                      ?.advertisement_active
                                                                                      ?.value
                                                                                : top?.status ===
                                                                                  "pending"
                                                                                ? entities
                                                                                      ?.advertisement_in_queue
                                                                                      ?.value
                                                                                : ""}
                                                                        </span>
                                                                        <div className="description">
                                                                            {top?.status ===
                                                                                "active" && (
                                                                                <dl>
                                                                                    <div className="description__row">
                                                                                        <dt>
                                                                                            {
                                                                                                entities
                                                                                                    ?.advertisement_start
                                                                                                    ?.value
                                                                                            }
                                                                                        </dt>
                                                                                        <dd>
                                                                                            {
                                                                                                top?.startsAt
                                                                                            }
                                                                                        </dd>
                                                                                    </div>
                                                                                    <div className="description__row">
                                                                                        <dt>
                                                                                            {
                                                                                                entities
                                                                                                    ?.advertisement_end
                                                                                                    ?.value
                                                                                            }
                                                                                        </dt>
                                                                                        <dd>
                                                                                            {
                                                                                                top?.endsAt
                                                                                            }
                                                                                        </dd>
                                                                                    </div>
                                                                                </dl>
                                                                            )}
                                                                            {top?.status ===
                                                                                "pending" && (
                                                                                <dl>
                                                                                    <div className="description__row">
                                                                                        <dt>
                                                                                            {
                                                                                                entities
                                                                                                    ?.common_to
                                                                                                    ?.value
                                                                                            }
                                                                                        </dt>
                                                                                        <dd>
                                                                                            {
                                                                                                top?.startsAt
                                                                                            }
                                                                                        </dd>
                                                                                    </div>
                                                                                </dl>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                )) || (
                                                                    <td className="is-empty" />
                                                                )}
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                        </section>
                    </div>
                </div>
            </main>
        </>
    );
};
